import { useCallback, useEffect, useMemo, useState } from 'react';
import { CohortInputPayload, FunnelCohortsApiClient } from '../api/cohortsClient';
import type { FunnelCohort, FunnelCohortCondition, FunnelCohortEstimate } from '../types';

interface UseCohortsInput {
  projectId: string;
  enabled: boolean;
}

interface UseCohortsResult {
  cohorts: FunnelCohort[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  createCohort: (input: CohortInputPayload) => Promise<FunnelCohort>;
  updateCohort: (cohortId: string, input: CohortInputPayload) => Promise<FunnelCohort>;
  deleteCohort: (cohortId: string) => Promise<void>;
  estimateConditions: (conditions: FunnelCohortCondition[], signal?: AbortSignal) => Promise<FunnelCohortEstimate>;
}

export function useCohorts(input: UseCohortsInput): UseCohortsResult {
  const [cohorts, setCohorts] = useState<FunnelCohort[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const client = useMemo(() => new FunnelCohortsApiClient(), []);

  useEffect(() => {
    if (!input.enabled || !input.projectId) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    client.list(input.projectId, controller.signal)
      .then(setCohorts)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load cohorts');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [client, input.enabled, input.projectId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

  const createCohort = useCallback(async (payload: CohortInputPayload) => {
    const cohort = await client.create(input.projectId, payload);
    refresh();
    return cohort;
  }, [client, input.projectId, refresh]);

  const updateCohort = useCallback(async (cohortId: string, payload: CohortInputPayload) => {
    const cohort = await client.update(input.projectId, cohortId, payload);
    refresh();
    return cohort;
  }, [client, input.projectId, refresh]);

  const deleteCohort = useCallback(async (cohortId: string) => {
    await client.delete(input.projectId, cohortId);
    refresh();
  }, [client, input.projectId, refresh]);

  const estimateConditions = useCallback(
    (conditions: FunnelCohortCondition[], signal?: AbortSignal) =>
      client.estimate(input.projectId, { conditions }, signal),
    [client, input.projectId],
  );

  return { cohorts, loading, error, refresh, createCohort, updateCohort, deleteCohort, estimateConditions };
}
